export interface IntentConfig {
  key: string;
  name: string;
  description: string;
  category: string;
  examples: string[];
  demoComponent?: string;
}

export const INTENT_CONFIGS: IntentConfig[] = [
  {
    key: 'download',
    name: 'Download Content',
    description: 'Download videos, audio, and other media from URLs',
    category: 'download',
    examples: [
      'video-download',
      'music-download',
      'audio-download',
      'media-download',
    ],
    demoComponent: 'DownloadDemo',
  },
  {
    key: 'summarize',
    name: 'Summarize Content',
    description: 'Generate summaries from videos, audio, and other content',
    category: 'summarize',
    examples: [
      'ai-summarizer',
      'video-summarizer',
      'youtube-summary-generator',
      'content-summarize',
    ],
    demoComponent: 'SummarizeDemo',
  },
  {
    key: 'notes',
    name: 'Take Notes',
    description:
      'Generate structured notes and bullet points from videos and content',
    category: 'notes',
    examples: ['note-taker', 'notetaker', 'notes', 'smart-notes', 'ai-notes'],
    demoComponent: 'NotesDemo',
  },
  {
    key: 'media-to-pdf',
    name: 'Convert Media to PDF',
    description:
      'Convert video or audio content into PDF documents with transcripts and summaries',
    category: 'media-to-pdf',
    examples: [
      'media-to-pdf',
      'convert-to-pdf',
      'video-to-pdf',
      'audio-to-pdf',
    ],
    demoComponent: 'ConvertVideoToPdfDemo',
  },
  {
    key: 'video-analyzer',
    name: 'Analyze Video',
    description:
      'Analyze video content for insights, patterns, and detailed information',
    category: 'video-analyzer',
    examples: ['video-analyzer'],
    demoComponent: 'VideoAnalyzerDemo',
  },
];

/**
 * Get intent configuration by category
 */
export const getIntentConfig = (category: string): IntentConfig | null => {
  return INTENT_CONFIGS.find((config) => config.category === category) || null;
};

/**
 * Get intent category from intent parameter
 */
export const getIntentCategoryFromParam = (intent: string): string | null => {
  const lowerIntent = intent.toLowerCase();

  // Convert media to PDF intents
  if (
    lowerIntent.includes('to-pdf') ||
    lowerIntent.includes('to-document') ||
    lowerIntent.includes('media-to-pdf')
  ) {
    return 'media-to-pdf';
  }

  // Download-related intents
  if (lowerIntent.includes('download')) {
    return 'download';
  }

  // Summarize-related intents
  if (lowerIntent.includes('summar')) {
    return 'summarize';
  }

  // Notes-related intents
  if (
    lowerIntent.includes('note-taker') ||
    lowerIntent.includes('notetaker') ||
    lowerIntent.includes('notes')
  ) {
    return 'notes';
  }

  // Video analyzer intents
  if (lowerIntent === 'video-analyzer') {
    return 'video-analyzer';
  }

  return null;
};

/**
 * Check if an intent parameter matches a category
 */
export const matchesIntentCategory = (
  intent: string,
  category: string
): boolean => {
  const intentCategory = getIntentCategoryFromParam(intent);
  return intentCategory === category;
};
